import { getBim360AdminUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalStore } from '@adsk/alloy-react-context';

import { readCachedData, writeCachedData } from '../helpers/cacheHelpers';
import { fetchWithAccountType } from '../helpers/fetchWithAccountType';
import { removeOldDuplicatesBy } from '../helpers/removeOldDuplicatesBy';

type Services = {
  service_type: string;
  display_name: string;
  service_category: string;
  service_access: string;
  service_domain: string;
  service_icon?: string;
  account_url?: string;
  admin_category?: string;
};

// See https://adminv1.api.b360-dev.autodesk.com/api#!/users/InternalV1_Users_get_project_entitlements
export type Account = {
  account_display_name: string;
  account_id: string;
  account_only_scope: string[];
  account_scope: string[];
  account_url: string;
  account_type?: string;
  idle_timeout?: {
    enabled: boolean;
    duration: number;
  };
  api_access: boolean;
  image_url: string;
  region: string;
  mfaBlocked?: boolean;
  services: Services[];
};

export type AccountsResponse = {
  current_account_id: string;
  accounts: Account[];
  record_count: number;
  offset: number;
  locale: string;
  next?: string;
};

type AccountEntitlementsStore = {
  data: null | AccountsResponse;
  error: null | unknown;
  loading: boolean;
  _started: boolean;
  setAccounts(data: AccountEntitlementsStore['data']): void;
  updateAccount(account: Account): void;
  addAccount(account: Account): void;

  // Fetch methods
  fetchData: () => void;
  retry: () => void;
};

// Custom fetch function for account entitlements with pagination
const fetchAccountEntitlements = async (): Promise<AccountsResponse> => {
  const BASE_URL = `${getBim360AdminUrl()}/v1/account_entitlements?service_categories=acc,next_gen,classic,admin&include_services=true&limit=200&offset=0`;

  let allAccounts: Account[] = [];
  let url: string | undefined = BASE_URL;
  let response: AccountsResponse;

  // Fetch all pages
  do {
    response = await fetchWithAccountType<AccountsResponse>(url);
    allAccounts = [...allAccounts, ...response.accounts];
    url = response.next
      ? `${getBim360AdminUrl()}/v1${response.next}`
      : undefined;
  } while (url);

  // Remove duplicates and return consolidated response
  const uniqueAccounts = removeOldDuplicatesBy(
    allAccounts,
    (account) => account.account_id,
  );

  return {
    ...response,
    accounts: uniqueAccounts,
    record_count: uniqueAccounts.length,
  };
};

export const useAccountEntitlementsStore = () =>
  getOrCreateGlobalStore<AccountEntitlementsStore>(
    (set, get) => ({
      data: null,
      error: null,
      loading: false,
      _started: false,

      setAccounts: (data: AccountEntitlementsStore['data']) => set({ data }),
      updateAccount: (updatedAccount: Account) => {
        const { data } = get();
        if (data) {
          const updatedAccounts = data.accounts.map((account: Account) =>
            account.account_id === updatedAccount.account_id
              ? updatedAccount
              : account,
          );
          set({
            data: {
              ...data,
              accounts: updatedAccounts,
            },
          });
        }
      },
      addAccount: (account: Account) => {
        const { data } = get();
        if (data) {
          const updatedAccounts = [account, ...data.accounts];
          set({
            data: {
              ...data,
              accounts: updatedAccounts,
              record_count: updatedAccounts.length,
            },
          });
        }
      },

      fetchData: () => {
        // Set _started immediately to prevent race conditions
        // Check must happen before async operation begins
        if (get()._started) {
          return;
        }

        // Set loading state and guard flag atomically
        set({ _started: true, loading: true, error: null });

        // Load cached data immediately before fetching (only if not already loading)
        // This provides instant UI feedback while fresh data loads
        const cachedData = readCachedData<AccountsResponse>(
          CONTEXT_NAMES.AccountEntitlementsContext,
        );
        if (cachedData) {
          set({ data: cachedData });
        }

        const performFetch = async () => {
          try {
            const response = await fetchAccountEntitlements();

            // Write to cache after successful fetch
            writeCachedData(CONTEXT_NAMES.AccountEntitlementsContext, response);

            set({
              data: response,
              error: null,
              loading: false,
            });
          } catch (error) {
            set({
              error: error as Error,
              loading: false,
            });
          }
        };

        void performFetch();
      },

      retry: () => {
        const { fetchData } = get();
        set({ _started: false });
        fetchData();
      },
    }),
    CONTEXT_NAMES.AccountEntitlementsContext,
  )();
